import type { TFunction } from 'i18next';
import * as yup from 'yup';
import type { StoreViewDto } from '@subscriptions/application/dtos';

export const SUBSCRIPTION_BLOCK_NAME_MAX_LENGTH = 120;

/**
 * Allowed characters for the block name:
 * Unicode letters (incluye acentos y ñ), digits, spaces, and `- _ ! ¡ < > ( )`.
 */
export const SUBSCRIPTION_BLOCK_NAME_PATTERN = /^[\p{L}0-9 _\-!¡<>()]+$/u;

/** Form values managed by the SubscriptionBlockForm. */
export type SubscriptionBlockFormValues = {
  name: string;
  storeUuids: string[];
  groupInvoice: boolean;
};

/**
 * Builds the yup schema for the subscription block form, using the provided
 * translation function for validation messages. The keys live under
 * `subscriptions:blockCreationWizard.create.validation.*`.
 *
 * `stores` powers the same-type check: all selected stores must share both
 * `interval` and `status`. The UI prevents invalid combinations by
 * disabling incompatible options; this validation is a defensive guard for
 * stale `defaultValues` or programmatic state changes.
 *
 * The minimum number of stores depends on the flow: creation requires at
 * least two stores (a block always groups two or more), while edition allows
 * a single store (a block can be trimmed down to one). The same form is
 * reused for both flows — the caller passes `isEdition` to switch the rule.
 *
 * @param t - i18next translation function bound to the `subscriptions` namespace
 * @param stores - Available stores used to resolve uuids during validation
 * @param isEdition - When `true`, allows single-store blocks (edition flow)
 * @returns Yup schema validating `SubscriptionBlockFormValues`
 */
export const createSubscriptionBlockFormSchema = (t: TFunction, stores: StoreViewDto[], isEdition = false) =>
  yup.object({
    name: yup
      .string()
      .trim()
      .max(SUBSCRIPTION_BLOCK_NAME_MAX_LENGTH, ({ max }) =>
        t('blockCreationWizard.create.validation.nameTooLong', { max }),
      )
      .matches(SUBSCRIPTION_BLOCK_NAME_PATTERN, {
        excludeEmptyString: true,
        message: t('blockCreationWizard.create.validation.nameInvalidChars'),
      })
      .required(t('blockCreationWizard.create.validation.nameRequired')),
    storeUuids: yup
      .array()
      .of(yup.string().required())
      .min(
        isEdition ? 1 : 2,
        isEdition
          ? t('blockCreationWizard.create.validation.storesRequiredEdition')
          : t('blockCreationWizard.create.validation.storesRequired'),
      )
      .test('sameType', t('blockCreationWizard.create.validation.storesMixedTypes'), (uuids) => {
        if (!uuids || uuids.length < 2) return true;
        const selected = stores.filter((store) => uuids.includes(store.uuid));
        if (selected.length < 2) return true;
        const reference = selected[0];
        return selected.every((store) => store.interval === reference.interval && store.status === reference.status);
      }),
    groupInvoice: yup.boolean().default(false),
  });
